import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { motion } from 'framer-motion';
import { Timer, X } from 'lucide-react';

/**
 * Modal that plays a 15-second form loop of the tapped exercise.
 * Uses framer-motion transforms on the same silhouette to hint at the movement,
 * with a hand-picked list of form cues and rep tempo per exercise category.
 */

// Motion config per category. Each returns a framer-motion `animate` + `transition`.
const MOTION_LIBRARY = {
  squat: {
    animate: { scaleY: [1, 0.78, 1], translateY: [0, 8, 0] },
    duration: 2.4,
    tempo: '1 rep every ~2.5s · 6 reps in 15s',
    cues: [
      'Feet shoulder-width, toes slightly out',
      'Push hips back like sitting into a chair, chest tall',
      'Drop until thighs are near parallel',
      'Drive through heels to stand — squeeze the glutes',
    ],
  },
  pushup: {
    animate: { scaleY: [1, 0.84, 1], skewX: [0, -3, 0] },
    duration: 2.2,
    tempo: '1 rep every ~2s · 7 reps in 15s',
    cues: [
      'Hands under shoulders, body in a straight line',
      'Lower slowly, elbows tucked ~45°',
      'Chest nearly kisses the floor',
      'Press up powerfully, keeping abs braced',
    ],
  },
  plank: {
    animate: { scale: [1, 1.02, 1], translateY: [0, -1, 0] },
    duration: 3.2,
    tempo: 'Hold steady · aim for 15-30s per set',
    cues: [
      'Forearms shoulder-width, elbows under shoulders',
      'Body is one straight line from head to heels',
      'Squeeze glutes and tuck ribs down',
      'Breathe slowly — hips neither sag nor pike',
    ],
  },
  lunge: {
    animate: { translateY: [0, 10, 0], scaleY: [1, 0.92, 1] },
    duration: 2.4,
    tempo: '1 rep every ~2.5s · 6 reps in 15s',
    cues: [
      'Big step forward, torso upright',
      'Drop back knee toward the floor',
      'Front knee tracks over front foot, not past toes',
      'Push through the front heel to return',
    ],
  },
  bridge: {
    animate: { translateY: [0, -10, 0] },
    duration: 2.4,
    tempo: '1 rep every ~2.5s · 6 reps in 15s',
    cues: [
      'Lie on your back, knees bent, feet flat',
      'Squeeze your glutes and lift hips to the sky',
      'Body forms a straight line — shoulders to knees',
      'Pause 1s at the top, then lower slowly',
    ],
  },
  curl: {
    animate: { rotate: [0, -8, 0] },
    duration: 2.0,
    tempo: '1 rep every ~2s · 7 reps in 15s',
    cues: [
      'Stand tall, elbows glued to your ribs',
      'Curl the weights up, squeezing the biceps',
      'Control the lowering phase — no swinging',
      'Full range: bottom stretch, top squeeze',
    ],
  },
  press: {
    animate: { translateY: [0, -10, 0] },
    duration: 2.2,
    tempo: '1 rep every ~2s · 7 reps in 15s',
    cues: [
      'Bar sits on your front delts, elbows slightly in front',
      'Brace core and glutes — no arching',
      'Press straight up, punch head through at the top',
      'Lower with control back to the shoulders',
    ],
  },
  row: {
    animate: { translateX: [0, -6, 0], rotate: [0, -2, 0] },
    duration: 2.2,
    tempo: '1 rep every ~2s · 7 reps in 15s',
    cues: [
      'Hinge at the hips, back flat, chest proud',
      'Pull the weight to your lower ribs, elbow back',
      'Squeeze shoulder blades together at the top',
      'Lower under control — feel the lats stretch',
    ],
  },
  cardio: {
    animate: { translateX: [-4, 4, -4], translateY: [0, -3, 0] },
    duration: 0.9,
    tempo: 'Steady rhythm · light effort for 15s+',
    cues: [
      'Land softly on the mid-foot, not the heel',
      'Slight forward lean from the ankles',
      'Arms swing naturally, shoulders relaxed',
      'Breathe in 2 steps, out 2 steps',
    ],
  },
  twist: {
    animate: { rotate: [0, 12, 0, -12, 0] },
    duration: 4.0,
    tempo: 'Hold each side ~15-20s · breathe deeply',
    cues: [
      'Sit tall, one leg crossed over the other',
      'Rotate slowly, leading from the ribs',
      'Anchor the opposite arm to your outer knee',
      'Lengthen on the inhale, deepen on the exhale',
    ],
  },
  forwardFold: {
    animate: { translateY: [0, 6, 0], scaleY: [1, 0.94, 1] },
    duration: 3.6,
    tempo: 'Hold and breathe · aim for 30-45s',
    cues: [
      'Feet hip-width, soft bend in the knees',
      'Hinge from the hips, let your torso hang',
      'Grab elbows or shins — release the neck',
      'Deepen on each exhale, don\'t force it',
    ],
  },
  hamstring: {
    animate: { rotate: [0, -3, 0], translateX: [0, 2, 0] },
    duration: 3.6,
    tempo: 'Hold each side ~20-30s · switch sides',
    cues: [
      'Sit tall, one leg extended straight',
      'Flex the toes up, keep the knee soft',
      'Hinge forward from the hips, chest to knee',
      'Feel a stretch — never sharp pain',
    ],
  },
  neck: {
    animate: { rotate: [0, -8, 0, 8, 0] },
    duration: 4.0,
    tempo: 'Slow · ~15-20s each side',
    cues: [
      'Sit tall, shoulders relaxed and down',
      'Gently tilt one ear toward the shoulder',
      'Add a light hand assist — no pulling',
      'Alternate sides, breathe slowly',
    ],
  },
  childPose: {
    animate: { scaleY: [1, 1.03, 1], translateY: [0, -2, 0] },
    duration: 4.0,
    tempo: 'Rest here · aim for 30-60s',
    cues: [
      'Kneel with big toes touching, knees wide',
      'Sit hips back onto your heels',
      'Reach the arms long, forehead to the mat',
      'Breathe into the low back and hips',
    ],
  },
  genericWorkout: {
    animate: { scale: [1, 1.03, 1] },
    duration: 2.6,
    tempo: 'Move with control · quality over speed',
    cues: [
      'Warm up first — 30s of easy movement',
      'Move slowly, feel the target muscle work',
      'Full range of motion, no locking joints',
      'Stop and rest if you feel sharp pain',
    ],
  },
  genericStretch: {
    animate: { rotate: [0, 4, 0, -4, 0] },
    duration: 5.0,
    tempo: 'Ease in and hold · 20-30s per side',
    cues: [
      'Ease into the stretch — no bouncing',
      'Breathe deeply and evenly',
      'Feel a mild tension, never sharp pain',
      'Hold long enough to let the tissue release',
    ],
  },
};

// Map an exercise name to a motion key
const KEY_RULES = [
  [/squat|goblet/i, 'squat'],
  [/push[- ]?up|press[- ]?up|chaturanga/i, 'pushup'],
  [/plank|hollow hold|core hold|dead bug|bird dog/i, 'plank'],
  [/lunge|split squat|step[- ]?up/i, 'lunge'],
  [/glute bridge|hip bridge|bridge|hip thrust/i, 'bridge'],
  [/curl|bicep|hammer/i, 'curl'],
  [/overhead|shoulder press|press$|military|leg press/i, 'press'],
  [/row|pull[- ]?down|pull[- ]?up|chin[- ]?up|deadlift|face pull|farmer/i, 'row'],
  [/run|jog|walk|cardio|hiit|burpee|jumping jack|skip|jump/i, 'cardio'],
  [/twist|rotation|spinal|thoracic|wood chop|cat[- ]?cow/i, 'twist'],
  [/forward fold|toe touch|standing fold/i, 'forwardFold'],
  [/hamstring|seated stretch|pike|figure[- ]?4/i, 'hamstring'],
  [/neck|upper trap|scalene/i, 'neck'],
  [/child.?pose|childs pose|pigeon|bow pose|cobra|downward dog|yoga|hip flexor/i, 'childPose'],
  [/stretch|mobility|flexibility|foam roll|open|release/i, 'genericStretch'],
];

const getMotion = (name = '') => {
  for (const [re, key] of KEY_RULES) if (re.test(name)) return MOTION_LIBRARY[key];
  return MOTION_LIBRARY.genericWorkout;
};

const AnimatedExerciseModal = ({ open, onOpenChange, name, tone = 'workout', why, sub, Sketch }) => {
  const conf = useMemo(() => getMotion(name || ''), [name]);
  const [remaining, setRemaining] = useState(15);

  useEffect(() => {
    if (!open) return;
    setRemaining(15);
    const t = setInterval(() => {
      setRemaining((r) => (r > 0 ? r - 1 : 0));
    }, 1000);
    return () => clearInterval(t);
  }, [open]);

  const bg = tone === 'stretch' ? 'bg-[#EEF2EA]' : 'bg-[#F2EFE9]';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md bg-[#FAF9F6] border-[#E5E1D8] rounded-3xl p-0 overflow-hidden"
        data-testid="animated-exercise-modal"
      >
        <DialogHeader className="px-6 pt-6 pb-2">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <span className="text-[11px] tracking-[0.2em] uppercase font-semibold text-[#7E9979]">
                {tone === 'stretch' ? 'Stretch · Form loop' : 'Workout · Form loop'}
              </span>
              <DialogTitle className="font-serif-display text-2xl font-medium text-[#1A2421] leading-tight mt-1 truncate">
                {name}
              </DialogTitle>
              {sub && <div className="text-xs text-[#4A5A53] mt-1">{sub}</div>}
            </div>
            <div className="flex items-center gap-1 text-xs tracking-[0.15em] uppercase font-semibold text-[#4A5A53] bg-white border border-[#E5E1D8] rounded-full px-3 py-1.5">
              <Timer className="w-3.5 h-3.5 text-[#4A7C59]" strokeWidth={1.8} />
              {remaining}s
            </div>
          </div>
        </DialogHeader>

        <div className={`mx-6 my-4 rounded-2xl ${bg} border border-[#E5E1D8] flex items-center justify-center aspect-square`}>
          <motion.div
            style={{ width: '68%', height: '68%' }}
            animate={conf.animate}
            transition={{ duration: conf.duration, ease: 'easeInOut', repeat: Infinity }}
            data-testid="animated-exercise-figure"
          >
            {Sketch && <Sketch />}
          </motion.div>
        </div>

        <div className="px-6 pb-6 space-y-3">
          <div className="text-xs tracking-[0.15em] uppercase font-semibold text-[#4A7C59]" data-testid="animated-exercise-tempo">
            {conf.tempo}
          </div>
          <ol className="space-y-2">
            {conf.cues.map((c, i) => (
              <li key={i} className="flex gap-3 items-start text-sm text-[#1A2421] leading-relaxed">
                <span className="shrink-0 w-6 h-6 rounded-full bg-[#4A7C59]/10 text-[#4A7C59] text-xs font-semibold flex items-center justify-center mt-0.5">
                  {i + 1}
                </span>
                <span className="font-light">{c}</span>
              </li>
            ))}
          </ol>
          {why && (
            <p className="text-xs italic text-[#7E9979] leading-relaxed pt-2 border-t border-[#E5E1D8]">
              Why for you: {why}
            </p>
          )}
          <p className="text-[11px] text-[#C05746] italic pt-1">
            Stop and consult your physician if you feel sharp pain.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AnimatedExerciseModal;
